import * as c from 'common/c'
import { cloneDeep, isEmpty } from 'es-toolkit/compat'

import { minWidth, styled } from '@mui/system'
import {
  Autocomplete,
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import { DataGrid } from '@mui/x-data-grid'
import {
  InfoOutlined as InfoOutlinedIcon,
  SubscriptionsOutlined as SubscriptionsIcon,
} from '@mui/icons-material'

import SearchField from 'components/search-field/index'
import ManageIntegrationComponent from 'components/admin/manage-integrations/index'
import { showModal } from 'actions/app'
import {
  createIntegration,
  type IntegrationOnboarding,
  manageIntegrationPermissions,
  requestIntegrationCode,
  unlinkTenantIntegration,
} from 'api/integration'
import { clearIntegrationData } from 'common/integrationUtils'
import colors from 'common/colors'
import { setIntegrationUrls } from 'actions/integrations'
import { getTranslatedName, isTokenValid } from 'common/misc'
import { subscriptionStatus } from '@farmtrx/options'
import { trackEvent } from 'api/usage-tracking'

import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useState, useCallback, type ReactElement, useEffect } from 'react'
import { useNavigate } from 'react-router'

import { getCurrentSubscription } from 'api/subscriptions'
import type { TenantIntegration } from 'state/integrations'
import type {
  IntegrationUrls,
  IntegrationRef,
  IntegrationData,
} from 'state/integrations'
import { type AppState } from 'state/index'

import EnableCFFConfirmation from './enableCffConfirmation'
import UnlinkIntegration from './unlink-integration'

const JDLogo = 'JDOC-tm-black.svg'
const TrimbleLogo = 'PTx_FarmEngage_Logo.png'
const FieldOpsLogo = 'fieldops.png'
export default function Integrations({
  availableIntegrations = [],
  enabledIntegrations = [],
}): ReactElement<any, any> {
  const baseURL: string = window.location.origin
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const [searchValue, setSearchValue] = useState<string>('')
  const navigate = useNavigate()
  const urls: {
    [key: string]: {
      IntegrationRef: IntegrationRef
      authenticated: boolean
      status: number
      fallbackLogout?: string
      availableConnections?: IntegrationUrls[]
    }
  } = useSelector(
    (state: AppState) => state.integrations.integrationUrls,
  ) as any

  const cffPreferences = useSelector(
    (state: AppState) => state.account.singleton.prefs?.cff,
  )

  const currentSubscription = useSelector(
    (state: AppState) => state.account.currentSubscription,
  )
  const permissions = useSelector(
    (state: AppState) => state.account.permissions,
  )
  const customClaims = useSelector(
    (state: AppState) => state.account.customClaims,
  )
  const isAdmin =
    permissions.includes('farmtrx:admin') || customClaims?.[c.FT_ADMIN_CLAIM]
  const isPartner =
    permissions.includes('partner:admin') || customClaims?.[c.FT_PARTNER_CLAIM]

  const [validSubscription, setValidSubscription] = useState(false)
  const [linkableOrgs, setLinkableOrgs] = useState(false)
  const [selectedOrg, setSelectedOrg] = useState<{
    [key: string]: IntegrationUrls
  }>({})
  useEffect(() => {
    if (isEmpty(currentSubscription)) {
      //@ts-expect-error
      dispatch(getCurrentSubscription())
    }
    const _validSubscription =
      currentSubscription?.subscriptionStatus === subscriptionStatus.FreeYear ||
      currentSubscription?.subscriptionStatus === subscriptionStatus.Paid ||
      currentSubscription?.subscriptionStatus === subscriptionStatus.Comped
    setValidSubscription(_validSubscription)
  }, [currentSubscription])

  const [selectedRowData, setSelectedRowData] =
    useState<IntegrationData | null>(null)
  const [selectedIntegration, setSelectedIntegration] =
    useState<IntegrationData | null>(null)

  useEffect(() => {
    const _selectedOrgs: { [key: string]: IntegrationUrls } = {}
    if (!isEmpty(urls)) {
      for (const key in urls) {
        const availableConnections = urls[key]?.availableConnections || []
        if (availableConnections.length > 0) {
          setLinkableOrgs(true)
          _selectedOrgs[key] = availableConnections[0]
        }
      }
      setSelectedOrg(_selectedOrgs)
    }
  }, [urls])

  const onConnectOrg = useCallback(
    (row) => {
      if (!isEmpty(selectedOrg)) {
        const customerId: string = selectedOrg[row.id].id
        const request = {
          integrationId: row.id,
          customerId,
          name: selectedOrg.name,
          status: c.ENABLED,
        }
        //@ts-ignore
        dispatch(createIntegration(request)).then((res) => {
          if (res.message === 'Integration Linked') {
            dispatch(
              setIntegrationUrls({
                [row.id]: {
                  [customerId]: selectedOrg[row.id],
                  authenticated: urls[row.id].authenticated,
                  status: urls[row.id].status,
                },
              }),
            )
          }
        })
      }
    },
    [selectedOrg],
  )

  const onChangeOrg = useCallback(
    (newValue, integrationId) => {
      const _selectedOrgs = cloneDeep(selectedOrg)
      _selectedOrgs[integrationId] = newValue
      setSelectedOrg(_selectedOrgs)
    },
    [selectedOrg],
  )

  const columns = [
    {
      field: 'name',
      headerName: t('product'),
      width: 250,
      renderCell: (params) => {
        //TODO: Update integration table to store if an integration is in beta or not.
        //Maybe add to tenant if they are participants in beta testing
        switch (params.row.name) {
          case 'operations_center':
            return (
              <img
                alt="John Deere Operations Center"
                src={`https://images.farmtrx.app/integrations/${JDLogo}`}
                width="205px"
              />
            )
          case 'trimble_ag':
            return (
              <img
                alt="FarmEngage"
                style={{ marginLeft: '19px', marginTop: '5px' }}
                src={`https://images.farmtrx.app/integrations/${TrimbleLogo}`}
                height="60px"
                width="180px"
              />
            )
          case 'field_ops_staging':
          case 'field_ops':
            return (
              <img
                alt="Case IH Field Operations"
                style={{ marginLeft: '19px', marginTop: '5px' }}
                src={`https://images.farmtrx.app/integrations/${FieldOpsLogo}`}
                width="180px"
              />
            )
          default:
            return getTranslatedName(params.row.name) + ` ${t('beta')}`
        }
      },
    },
    {
      field: 'linkedOrg',
      headerName: t('linked_organization'),
      width: 600,
      flex: 1,
      renderCell: (params) => {
        const availableIntegration: TenantIntegration[] =
          enabledIntegrations.filter(
            (integration: TenantIntegration) =>
              integration?.integrationId === params.row.id,
          )
        const integrationUrl = urls?.[params.row.id]
        const activatedIntegration =
          availableIntegration.length > 0 ? availableIntegration[0] : null
        let name = activatedIntegration?.name || ''
        const authenticated = urls?.[params.row.id]?.authenticated || false
        if (
          activatedIntegration &&
          integrationUrl &&
          integrationUrl[activatedIntegration.customerId]
        ) {
          name = integrationUrl[activatedIntegration.customerId].name
        } else if (
          isEmpty(integrationUrl) &&
          availableIntegration.length > 0 &&
          authenticated
        ) {
          name = t('no_linked_organization')
        }
        return availableIntegration.length > 0 ? (
          <Typography
            variant="h5"
            sx={{
              padding: 1,
              fontWeight: 500,
              fontSize: 'medium',
              width: 500,
            }}
            style={{ display: 'inline-block', whiteSpace: 'pre-line' }}
          >
            {availableIntegration[0]?.status === c.ENABLED
              ? name
              : t('reactivate_for_linked_org', {
                  linkedOrganization: name,
                })}
          </Typography>
        ) : (
          <Typography
            variant="h5"
            sx={{
              padding: 1,
              fontWeight: 500,
              fontSize: 'medium',
              width: 500,
            }}
            style={{ display: 'inline-block', whiteSpace: 'pre-line' }}
          >
            {name}
          </Typography>
        )
      },
    },
    {
      field: 'action',
      headerName: t('action'),
      sortable: false,
      maxWidth: 360,
      flex: 1,
      renderCell: (params) => {
        const activeIntegration: TenantIntegration[] =
          enabledIntegrations.filter(
            (integration: TenantIntegration) =>
              integration?.integrationId === params.row.id &&
              integration?.status === c.ENABLED,
          )
        const disabledIntegration = enabledIntegrations.filter(
          (integration: TenantIntegration) =>
            integration?.integrationId === params.row.id &&
            integration?.status === c.DISABLED,
        )
        const integrationUrl = urls?.[params.row.id]
        const activatedIntegration =
          activeIntegration.length > 0 ? activeIntegration[0]?.customerId : null
        let hasActiveIntegration = false
        const token: string = localStorage.getItem(params.row.name) || ''
        const parsedToken = token === '' ? undefined : JSON.parse(token)
        const tokenValid: boolean = isTokenValid(
          parsedToken?.accessToken,
          parsedToken?.expiresAt,
        )
        const id = params.row.id
        const authenticated = (urls?.[id]?.authenticated && tokenValid) || false

        if (authenticated) {
          if (
            activatedIntegration &&
            integrationUrl &&
            integrationUrl[activatedIntegration]
          ) {
            hasActiveIntegration = true
          }
        } else {
          if (activatedIntegration) {
            hasActiveIntegration = true
          }
        }
        const availableConnections = urls?.[id]?.availableConnections || []
        if (activeIntegration.length > 0) {
          return (
            <>
              {hasActiveIntegration ? (
                <StyledButton
                  onClick={() =>
                    onManageIntegration(
                      params.row,
                      activeIntegration,
                      hasActiveIntegration,
                    )
                  }
                  variant="contained"
                  color="primary"
                  disabled={!validSubscription || isAdmin || isPartner}
                >
                  {authenticated ? t('manage') : t('login')}
                </StyledButton>
              ) : null}
              {authenticated ? (
                <StyledButton
                  onClick={() => logoutIntegration(params.row)}
                  variant="contained"
                  color="primary"
                  disabled={!validSubscription || isAdmin || isPartner}
                >
                  {t('logout')}
                </StyledButton>
              ) : null}
              {hasActiveIntegration ? (
                <StyledButton
                  onClick={() => {
                    setSelectedIntegration(params.row)
                  }}
                  variant="contained"
                  color="secondary"
                  disabled={!validSubscription || isAdmin || isPartner}
                >
                  {t('unlink')}
                </StyledButton>
              ) : null}
            </>
          )
        } else if (availableConnections.length > 0) {
          return (
            <>
              <Autocomplete
                disableClearable
                disabled={!validSubscription}
                value={selectedOrg[params.row.id] || null}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                style={{
                  maxHeight: '40px',
                  minWidth: '120px',
                  width: '75%',
                  marginRight: '10px',
                }}
                sx={{
                  '& input': {
                    height: '1em',
                  },
                  '& label': {
                    lineHeight: '1.5em',
                    fontSize: '0.75em',
                  },
                }}
                id={`set-all-farmtrx-clients`}
                autoHighlight
                size="small"
                options={availableConnections}
                defaultValue={availableConnections[0]}
                selectOnFocus
                getOptionLabel={(option) => option.name || ''}
                onChange={(event, newValue, reason) => {
                  onChangeOrg(newValue, params.row.id)
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={t('select_client')}
                    label={t('farmtrx-client')}
                    style={{ width: '100%' }}
                  />
                )}
              />
              <StyledButton
                onClick={() => onConnectOrg(params.row)}
                variant="contained"
                color="primary"
                disabled={!validSubscription || isEmpty(selectedOrg)}
              >
                {t('connect')}
              </StyledButton>
            </>
          )
        }
        return (
          <>
            <StyledButton
              onClick={() => {
                setSelectedRowData(params.row)
              }}
              variant="contained"
              color="primary"
              disabled={
                !validSubscription ||
                (enabledIntegrations.length > 0 &&
                  disabledIntegration.length === 0) ||
                isAdmin ||
                isPartner ||
                linkableOrgs
              }
            >
              {disabledIntegration.length === 0
                ? t('activate')
                : t('reactivate')}
            </StyledButton>

            {authenticated ? (
              <StyledButton
                onClick={() => logoutIntegration(params.row)}
                variant="contained"
                color="primary"
                disabled={!validSubscription || isAdmin || isPartner}
              >
                {t('logout')}
              </StyledButton>
            ) : null}
            {authenticated ? (
              <StyledButton
                onClick={() => {
                  setSelectedIntegration(params.row)
                }}
                variant="contained"
                color="secondary"
                disabled={!validSubscription || isAdmin || isPartner}
              >
                {t('unlink')}
              </StyledButton>
            ) : null}
          </>
        )
      },
    },
  ]

  const filterIntegrations = (searchValue, integration) => {
    if (searchValue) {
      const lowerSearchValue = searchValue.toLowerCase()

      return integration.name?.toLowerCase().indexOf(lowerSearchValue) > -1
    }

    return true
  }

  const handleEnableCffShowModal = () => {
    dispatch(
      showModal(
        <EnableCFFConfirmation onResult={handleEnableCffShowModalResult} />,
      ),
    )
  }

  const handleEnableCffShowModalResult = useCallback(
    (result: 'apply' | 'close') => {
      if (
        result === 'apply' &&
        selectedRowData !== null &&
        selectedRowData !== undefined
      ) {
        redirectToIntegration(selectedRowData! as IntegrationOnboarding)
      } else {
        setSelectedRowData(null)
      }
    },
    [selectedRowData],
  )

  const handleUnlinkIntegrationShowModal = () => {
    const name = selectedIntegration?.name || ''
    dispatch(
      showModal(
        <UnlinkIntegration
          onResult={handleUnlinkIntegrationShowModalResult}
          integrationName={t(name) || ''}
        />,
      ),
    )
  }

  const handleUnlinkIntegrationShowModalResult = useCallback(
    (result: 'apply' | 'close') => {
      if (result === 'apply' && selectedIntegration && selectedIntegration.id) {
        const availableIntegration: TenantIntegration[] =
          enabledIntegrations.filter(
            (integration: TenantIntegration) =>
              integration?.integrationId === selectedIntegration.id,
          )
        const activatedIntegration =
          availableIntegration.length > 0 ? availableIntegration[0] : null
        //@ts-expect-error
        dispatch(unlinkTenantIntegration(selectedIntegration.id)).then(() => {
          if (urls != undefined && urls[selectedIntegration.id] != undefined) {
            const integrationUrls: IntegrationRef =
              urls[selectedIntegration.id]?.IntegrationRef

            if (activatedIntegration) {
              const integration: IntegrationUrls | undefined =
                integrationUrls &&
                integrationUrls[activatedIntegration?.customerId]

              const token: string =
                localStorage.getItem(selectedIntegration.name) || ''
              const parsedToken = token === '' ? undefined : JSON.parse(token)
              const tokenValid: boolean = isTokenValid(
                parsedToken?.accessToken,
                parsedToken?.expiresAt,
              )
              if (parsedToken?.accessToken && tokenValid)
                if (integration && integration.managePermissions)
                  manageIntegrationPermissions({
                    connectionUri: integration.managePermissions,
                    token: parsedToken?.accessToken,
                    integrationId: selectedIntegration.id,
                  })
            }
            if (urls[selectedIntegration.id]?.fallbackLogout) {
              window.open(
                urls[selectedIntegration.id].fallbackLogout,
                '_blank',
                'noopener,noreferrer',
              )
            }
            clearIntegrationData()
            trackEvent('integration_unlinked', {
              integration_name: selectedIntegration.name,
            })
          }
        })
      } else {
        setSelectedIntegration(null)
      }
    },
    [selectedIntegration, urls],
  )

  useEffect(() => {
    if (selectedRowData) {
      onActivateIntegration(selectedRowData)
    }
  }, [selectedRowData])

  const onActivateIntegration = useCallback(
    (rowData) => {
      if (cffPreferences === undefined || cffPreferences.enabled === false) {
        handleEnableCffShowModal()
        return
      } else {
        redirectToIntegration(rowData)
      }
    },
    [selectedRowData],
  )

  useEffect(() => {
    if (selectedIntegration) {
      onUnlinkIntegration()
    }
  }, [selectedIntegration])

  const onUnlinkIntegration = useCallback(() => {
    handleUnlinkIntegrationShowModal()
  }, [selectedIntegration])

  const redirectToIntegration = (rowData: IntegrationOnboarding) => {
    //Call api endpoint to activate the integration.
    if (selectedRowData === null) {
      return
    }
    rowData.attempt = 1
    rowData.doOnboarding = true
    rowData.redirectUrl = baseURL
    if (
      rowData.name === 'trimble_ag' &&
      window.location.origin === 'http://localhost:3000'
    ) {
      rowData.redirectUrl = 'https://mydom.farmtrx-development.com'
    }
    localStorage.setItem('integrationLoginAttempt', JSON.stringify(rowData))
    requestIntegrationCode(
      rowData.authorizationUrl,
      rowData.scopes,
      rowData.clientId,
      rowData.redirectUrl,
      rowData.tokenUrl,
      rowData.authParams ?? {},
      rowData.authType,
    )
  }

  const onManageIntegration = useCallback(
    (data, activeIntegration, hasActiveIntegration) => {
      const token: string = localStorage.getItem(data.name) || ''
      const parsedToken = token === '' ? undefined : JSON.parse(token)
      const tokenValid: boolean = isTokenValid(
        parsedToken?.accessToken,
        parsedToken?.expiresAt,
      )
      if (parsedToken?.accessToken && tokenValid) {
        const translatedName = getTranslatedName(data.name)
        if (hasActiveIntegration) {
          dispatch(
            showModal(
              <ManageIntegrationComponent
                token={parsedToken?.accessToken}
                integrationId={data.id}
                enabledClients={activeIntegration}
                name={data.name}
                expiresAt={parsedToken?.expiresAt}
              />,
              {
                title: t('manage_integration', {
                  integration_name: translatedName,
                }),
              },
            ),
          )
        }
      } else if (hasActiveIntegration) {
        data.attempt = 1
        data.doOnboarding = true
        data.redirectUrl = baseURL

        if (
          data.name === 'trimble_ag' &&
          window.location.origin === 'http://localhost:3000'
        ) {
          data.redirectUrl = 'https://mydom.farmtrx-development.com'
        }
        localStorage.setItem('integrationLoginAttempt', JSON.stringify(data))
        requestIntegrationCode(
          data.authorizationUrl,
          data.scopes,
          data.clientId,
          data.redirectUrl,
          data.tokenUrl,
          data.authParams ?? {},
          data.authType,
        )
      }
    },
    [],
  )

  const logoutIntegration = useCallback(
    (integrationData) => {
      if (urls[integrationData.id]?.fallbackLogout) {
        window.open(
          urls[integrationData.id].fallbackLogout,
          '_blank',
          'noopener,noreferrer',
        )
      }
      clearIntegrationData()
    },
    [urls],
  )

  const onSearch = useCallback(
    (value) => {
      setSearchValue(value)
    },
    [searchValue],
  )

  function CustomNoRowsOverlayUsers() {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
        }}
      >
        {t('no_integrations_found')}
      </div>
    )
  }

  return (
    <Box
      sx={{
        width: 1,
        display: 'flex',
        flexDirection: 'column',
        padding: '1rem',
        height: '100%',
      }}
    >
      <h1>{t('third_party_integrations')}</h1>
      <Typography
        variant="h5"
        sx={{
          padding: 1,
          fontWeight: 500,
          fontSize: 'medium',
          color: isAdmin ? 'red' : colors.successAlert,
        }}
      >
        <IconButton
          size="small"
          edge="start"
          color="inherit"
          aria-label="menu"
          sx={{ mr: 1, ml: 1 }}
        >
          <InfoOutlinedIcon
            sx={{
              '& path': {
                fill: isAdmin ? 'red' : colors.successAlert,
              },
            }}
          />
        </IconButton>
        {isAdmin
          ? t('admin_third_party_integration_disabled')
          : t('one_third_party_integration_allowed')}
      </Typography>
      {
        //@ts-expect-error
        <SearchField
          sx={{ width: '100%' }}
          name={searchValue}
          placeholder={t('search')}
          onSearch={onSearch}
        />
      }
      {!validSubscription ? (
        <Box
          sx={{
            borderLeft: `8px solid ${alpha(colors.dangerAccent, 0.42)} !important`,
            background: `${alpha(colors.dangerAccent, 0.1)} !important`,
            color: 'red !important',
            fontWeight: 'bold !important',
            paddingLeft: '4px',
          }}
          title={t('no_subscriptions_activate_manage')}
          onClick={() => navigate('/admin/subscription')}
        >
          <span> {t('renew_subscription')}</span>
          {
            //@ts-ignore
            <SubscriptionsIcon
              size="small"
              style={{
                color: colors.dangerAccent,
                height: '0.7em',
                verticalAlign: 'sub',
              }}
              titleAccess={t('no_subscriptions_activate_manage')}
            />
          }
        </Box>
      ) : null}
      <div
        style={{
          flexGrow: 1,
          height: '100%',
          width: '100%',
          maxHeight: '800px',
          marginTop: '2rem',
        }}
      >
        <DataGrid
          autoHeight
          slots={{
            noRowsOverlay: CustomNoRowsOverlayUsers,
          }}
          rows={availableIntegrations
            .map((i) => {
              return i
            })
            .filter((i) => filterIntegrations(searchValue, i))}
          columns={columns}
          sx={{
            '& .MuiDataGrid-columnHeader': {
              backgroundColor: colors.greyBackground,
              textTransform: 'uppercase',
            },
          }}
          initialState={{
            pagination: { paginationModel: { page: 0, pageSize: 20 } },
          }}
          pageSizeOptions={[10, 20, 50]}
          rowHeight={75}
        />
      </div>
    </Box>
  )
}

const StyledButton = styled(Button)({
  marginTop: '0.5em',
  marginBottom: '0.5em',
  width: 'fit-content',
  marginRight: '10px',
})
